import { useRef, useState } from 'react'
import { CoinsIcon, InfoIcon, NewwalletIcon } from 'components/ui-kit/Icons/svg/index'
import CustomTooltip from 'components/ui-kit/Tooltip/index'
import { TOOLTIP_VARIANT } from 'constants/index'
import { useClickOutside } from 'hooks/useClickOutside'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useWindowSize } from 'react-use'
import { setShowCashierPopup } from 'redux-thunk/redux/slices/settings.slice'
import { formatPrice, getPrecision } from 'utils/helperFunctions.utils'

const PRECISION_COUNT = 2

const BalanceSwitch = ({ checked }) => (
  <div className='relative h-6 w-10 shrink-0'>
    <input
      type='checkbox'
      name='checkbox'
      checked={checked}
      readOnly
      className='peer absolute inset-0 z-10 m-0 h-full w-full cursor-pointer opacity-0'
    />
    <span className='pointer-events-none absolute inset-0 rounded-full bg-[var(--gray-80)] transition-colors duration-[400ms] peer-checked:bg-[var(--live-green)]' />
    <span className='pointer-events-none absolute left-[3px] top-1 h-4 w-4 rounded-full bg-[var(--gray-40)] transition-transform duration-[400ms] peer-checked:translate-x-[19px] peer-checked:bg-[var(--gray-90)]' />
  </div>
)

const CurrencyDropdown = (props) => {
  const { sidenavExpand, isBalanceOpen, onToggleBalance, onCloseBalance } = props
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { width } = useWindowSize()
  const { selectedWallet } = useSelector((state) => state.user)
  const { showSideNav } = useSelector(state => state.settings)
  const dropdownRef = useRef(null)

  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const isControlled = typeof isBalanceOpen === 'boolean'
  const isDropdownOpen = isControlled ? isBalanceOpen : uncontrolledOpen

  const closeDropdown = () => {
    if (isControlled) {
      onCloseBalance?.()
      return
    }
    setUncontrolledOpen(false)
  }

  useClickOutside(dropdownRef, () => {
    if (isDropdownOpen) closeDropdown()
  })

  const currencySymbol = selectedWallet?.currency?.isFiat
    ? selectedWallet?.currency?.symbol
    : '$'

  const formattedAmount = selectedWallet
    ? `${currencySymbol}${formatPrice(getPrecision(parseFloat(selectedWallet?.amount), PRECISION_COUNT), false, PRECISION_COUNT)}`
    : '$0.00'

  return (
    <div ref={dropdownRef} className='relative flex items-center max-[576px]:mx-2'>
      <button
        className='inline-flex min-h-11 cursor-pointer items-center justify-center border-0 bg-[var(--darkGray)] px-2 py-1 text-white no-underline after:hidden hover:bg-[var(--gray-80)] rounded-lg max-sm:min-h-10 max-sm:px-2 max-sm:py-1'
        type='button'
        id='currencies_dropdown_button'
        // onClick={() => {
        //   if (width < 768) {
        //     showSideNav && sidenavExpand()
        //   }
        //   if (isControlled) {
        //     onToggleBalance?.()
        //     return
        //   }
        //   setUncontrolledOpen(!uncontrolledOpen)
        // }}
      >
        <div className='flex items-center'>
          <span className='shrink-0'>
            <img
              src='/assets/images/coin_wallet.png'
              alt='coin'
              className='mr-2 h-5 w-5 max-[576px]:mr-0'
            />
          </span>
          <div className='mr-4 flex items-center justify-center text-base font-medium max-sm:mr-2 max-sm:text-xs'>
            {formattedAmount}
          </div>
        </div>
      </button>
      {/* <div
        className='flex h-12 w-12 shrink-0 cursor-pointer items-center justify-center rounded-r-[6px] bg-[var(--gray-80)] hover:bg-[rgb(var(--color-warning-soft)/var(--tw-bg-opacity,1))] max-sm:h-10 max-sm:w-10 [&_svg]:h-5 [&_svg]:w-5 max-sm:[&_svg]:h-4 max-sm:[&_svg]:w-4'
        onClick={() => { dispatch(setShowCashierPopup(true)) }}
      >
        <NewwalletIcon />
      </div> */}
      <div
        className={`${isDropdownOpen ? 'block' : 'hidden'} absolute top-full z-[1000] min-w-[368px] rounded-xl bg-[var(--gray-90)] p-4 mt-4 text-[var(--white)] shadow-[0_0_1.25rem_0_var(--darkGray-40),0_0.5rem_1rem_0_var(--darkGray-40)] max-md:p-4 max-[576px]:min-w-[340px]`}
        aria-labelledby='currencies_dropdown'
        id='currencies_dropdown'
      >
        <div className='mb-7 flex items-center justify-between text-base'>
          <div className='flex items-center justify-between gap-3'>
            <div className='font-bold uppercase leading-7'>{t('balances')}</div>
            <button
              className='hidden h-8 w-8 min-h-0 items-center justify-center rounded p-2'
              onClick={() => dispatch(setShowCashierPopup(true))}
            >
              +
            </button>
          </div>
          <div className='font-medium'>
            {t('total')}:
            <span className='text-[var(--live-green)]'>
              {` ${formattedAmount}`}
            </span>
          </div>
        </div>
        <div className='mb-2 rounded-lg bg-[var(--darkGray)] px-4 py-3 text-sm font-semibold'>
          <div className='flex items-center gap-3'>
            <BalanceSwitch checked />
            <div>
              {t('cashBalance')}
              <div>
                <CoinsIcon />
                <span className='ms-2 font-normal'>
                  {` ${formattedAmount}`}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className='mb-2 rounded-lg bg-[var(--darkGray)] px-4 py-3 text-sm font-semibold'>
          <div className='flex items-center gap-3'>
            <CustomTooltip
              variant={TOOLTIP_VARIANT.DEFAULT}
              message='Coming soon'
              showTooltip
              style={{ maxWidth: '280px' }}
            >
              <BalanceSwitch checked={false} />
            </CustomTooltip>
            <div>
              {t('nonCashBalance')}
              <CustomTooltip
                variant={TOOLTIP_VARIANT.DEFAULT}
                message='When selected, bets are placed using your bonus balance before your cash balance. If the bet wins, the winnings are credited to your cash balance.'
                showTooltip
                style={{ maxWidth: '280px' }}
              >
                <span className='inline-flex [&_svg]:ml-[5px]'><InfoIcon /></span>
              </CustomTooltip>
              <div>
                <CoinsIcon /> <span className='ms-2 font-normal'>${selectedWallet?.nonCashAmount}</span>
              </div>
            </div>
          </div>
        </div>
        <div className='rounded-lg bg-[var(--darkGray)] px-4 py-3 text-sm font-semibold'>
          <div className='flex items-center gap-3'>
            <div>
              {t('bonusBalance')}
              <CustomTooltip
                variant={TOOLTIP_VARIANT.DEFAULT}
                message='When selected, bets are placed using your bonus balance before your cash balance. If the bet wins, the winnings are credited to your cash balance.'
                showTooltip
                style={{ maxWidth: '280px' }}
              >
                <span className='inline-flex [&_svg]:ml-[5px]'><InfoIcon /></span>
              </CustomTooltip>
              <div>
                <CoinsIcon /> <span className='ms-2 font-normal'>${selectedWallet?.bonusBalance}</span>
              </div>
            </div>
          </div>
        </div>
        {/* <div className='balance-box'>
          <div className='flex items-center gap-3 coins '>
            <img src={walletBalanceCoins} />
            <div>
              Coinbatting XP
              <CustomTooltip
                variant={TOOLTIP_VARIANT.DEFAULT}
                message='Coming soon'
                showTooltip
              ><span className='info-icon'><InfoIcon /></span>
              </CustomTooltip>
              <div>
                <span className='normal-amount'>Coming Soon</span>
              </div>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  )
}

export default CurrencyDropdown
